import { RuleTypeEnum } from "../../../domain/RuleType";
import { WebUrlRuleValue } from "../../../domain/ruleValue/WebUrlRuleValue";
import { RuleValueStore, SerializedRuleValueStore } from "../RuleValueStore";

export class WebUrlRuleValueStore implements RuleValueStore<RuleTypeEnum.WEB_URL> {
    allowedExact: Map<string, URL[]>;
    deniedExact: Map<string, URL[]>;
    allowedStartsWith: Map<string, URL[]>;
    deniedStartsWith: Map<string, URL[]>;

    constructor() {
        this.allowedExact = new Map();
        this.deniedExact = new Map();
        this.allowedStartsWith = new Map();
        this.deniedStartsWith = new Map();
    }

    add(ruleValue: WebUrlRuleValue): void {
        const targetMap = ruleValue.action.isAllow()
            ? (ruleValue.matchType.isExact() ? this.allowedExact : this.allowedStartsWith)
            : (ruleValue.matchType.isExact() ? this.deniedExact : this.deniedStartsWith);

        const hostname = ruleValue.webUrl.hostname;
        const urls = targetMap.get(hostname) || [];
        urls.push(ruleValue.webUrl);
        targetMap.set(hostname, urls);
    }

    serialize(): SerializedRuleValueStore<RuleTypeEnum.WEB_URL> {
        const data: SerializedWebUrlRuleValueStoreData = {
            allowedExact: Array.from(this.allowedExact.values()).flat().map((webUrl: URL) => webUrl.href),
            deniedExact: Array.from(this.deniedExact.values()).flat().map((webUrl: URL) => webUrl.href),
            allowedStartsWith: Array.from(this.allowedStartsWith.values()).flat().map((webUrl: URL) => webUrl.href),
            deniedStartsWith: Array.from(this.deniedStartsWith.values()).flat().map((webUrl: URL) => webUrl.href),
        };
        return {
            type: RuleTypeEnum.WEB_URL,
            data: data,
        };
    }

    fromSerialized(serialized: SerializedRuleValueStore<RuleTypeEnum.WEB_URL>): void {
        this.allowedExact = this.rebuildMap(serialized.data.allowedExact);
        this.deniedExact = this.rebuildMap(serialized.data.deniedExact);
        this.allowedStartsWith = this.rebuildMap(serialized.data.allowedStartsWith);
        this.deniedStartsWith = this.rebuildMap(serialized.data.deniedStartsWith);
    }

    evaluateWebUrl(url: URL): WebUrlRuleValueStoreEvaluationResult {
        const hostnameMatchInAllowedExact: URL[] | undefined = this.allowedExact.get(url.hostname);
        if (hostnameMatchInAllowedExact) {
            const urlMatches: boolean = this.urlExactMatch(url, hostnameMatchInAllowedExact);
            if (urlMatches) return "ALLOWED";
        }

        const hostnameMatchInAllowedStartsWith: URL[] | undefined = this.allowedStartsWith.get(url.hostname);
        if (hostnameMatchInAllowedStartsWith) {
            const urlMatches: boolean = this.urlStartsWithMatch(url, hostnameMatchInAllowedStartsWith);
            if (urlMatches) return "ALLOWED";
        }

        const hostnameMatchInDeniedExact: URL[] | undefined = this.deniedExact.get(url.hostname);
        if (hostnameMatchInDeniedExact) {
            const urlMatches: boolean = this.urlExactMatch(url, hostnameMatchInDeniedExact);
            if (urlMatches) return "DENIED";
        }

        const hostnameMatchInDeniedStartsWith: URL[] | undefined = this.deniedStartsWith.get(url.hostname);
        if (hostnameMatchInDeniedStartsWith) {
            const urlMatches: boolean = this.urlStartsWithMatch(url, hostnameMatchInDeniedStartsWith);
            if (urlMatches) return "DENIED";
        }

        return "NO_MATCH";
    }

    private urlStartsWithMatch(url: URL, urlsToMatch: URL[]): boolean {
        for (const urlToMatch of urlsToMatch) {
            // Check hostname matches exactly with the rule
            if (url.hostname !== urlToMatch.hostname) continue;

            // Check if path starts with the rule path
            if (url.pathname.startsWith(urlToMatch.pathname)) return true;
        }
        return false;
    }

    private urlExactMatch(url: URL, urlsToMatch: URL[]): boolean {
        for (const urlToMatch of urlsToMatch) {
            // Check hostname and path match exactly with the rule
            if (url.hostname !== urlToMatch.hostname) continue;
            if (url.pathname !== urlToMatch.pathname) continue;

            // Check if url matches all the query params in the rule (can have more)
            if (this.queryParamsMatch(url.searchParams, urlToMatch.searchParams)) return true;
        }
        return false;
    }

    private queryParamsMatch(queryParams: URLSearchParams, queryParamsToMatch: URLSearchParams): boolean {
        for (const [key, value] of queryParamsToMatch.entries()) {
            if (queryParams.get(key) !== value) return false;
        }
        return true;
    }

    private rebuildMap(urls: string[]): Map<string, URL[]> {
        const map: Map<string, URL[]> = new Map();
        for (const url of urls) {
            const urlObject: URL = new URL(url);
            const existingUrls: URL[] = map.get(urlObject.hostname) || [];
            existingUrls.push(urlObject);
            map.set(urlObject.hostname, existingUrls);
        }
        return map;
    }
}

export interface SerializedWebUrlRuleValueStoreData {
    allowedExact: string[];
    deniedExact: string[];
    allowedStartsWith: string[];
    deniedStartsWith: string[];
};

export type WebUrlRuleValueStoreEvaluationResult = "ALLOWED" | "DENIED" | "NO_MATCH"